/**
 * A DESIGNED icon as portable metadata (#788).
 *
 * Its own module because it is not UI: the editor's dialog and the chat card
 * both package an icon this way, and the card must be able to import it without
 * dragging the whole editor stack into the chat's bundle.
 */
import { portableGlyphFor, type IconNode, type PortableIconSpec, type PortablePdappMetadata } from '@papan/types/miniapp-icon-svg'
import { resolveMiniappIconGlyph } from '@papan/core-ui/ui'
import type { MiniappIconSpec } from '@papan/types'

/** The `name` a designed icon travels under. See `designedIconMetadata`. */
const DESIGNED_ICON_NAME = 'Icon'

/**
 * A DESIGNED icon as portable metadata: the spec, plus the two things a PURE
 * renderer cannot go and fetch for itself.
 *
 *   • uploaded art — inlined as `data:` URIs by the editor's produce mode (there
 *     is no app folder to write it to, and the metadata has to stand alone);
 *   • a named Lucide glyph's GEOMETRY — the server export resolves it with
 *     `resolveLucideNode`, and in the browser `resolveMiniappIconGlyph` is the
 *     same answer through the same node-identity rule. What to emit when it does
 *     NOT resolve is `portableGlyphFor`'s to say, and both producers ask it.
 *
 * `name` is a fallback the emitter uses when a spec has no foreground of its own
 * (a designed spec always has one, or is deliberately blank, which draws the PDApp
 * mark) AND it is the rendered `<svg>`'s `aria-label`. Nothing else reads it, so
 * it is a constant rather than a field the author has to fill in; it must be the
 * SAME constant the glyph was resolved under, or the two disagree about which
 * foreground they are describing. A screen reader announces "Icon", which is what
 * a decorative tile with no app behind it is.
 */
export function designedIconMetadata(
  spec: MiniappIconSpec,
  assets: { bg?: string; fg?: string },
  glyph: IconNode | null,
): PortablePdappMetadata {
  const icon: PortableIconSpec = { ...spec }
  if (spec.icon) Object.assign(icon, portableGlyphFor(spec.icon, glyph))
  if (assets.bg || assets.fg) {
    icon.assets = { ...(assets.bg ? { bg: assets.bg } : {}), ...(assets.fg ? { fg: assets.fg } : {}) }
  }
  return { name: DESIGNED_ICON_NAME, icon }
}

/** `designedIconMetadata` with the glyph resolved — the whole job, in the browser.
 *
 *  Split in two so the assembly above can be TESTED in both directions: the
 *  resolver reaches Lucide through a bundler-only subpath (`ensureLucideIconNode`
 *  omits the `.mjs` Node needs), so under vitest every name resolves to `null` and
 *  a single async function could only ever exercise the substituted-glyph half. */
export async function metadataFromDesignedIcon(
  spec: MiniappIconSpec,
  assets: { bg?: string; fg?: string },
): Promise<PortablePdappMetadata> {
  return designedIconMetadata(spec, assets, await resolveMiniappIconGlyph(spec, DESIGNED_ICON_NAME))
}

/**
 * Does this spec reference uploaded art that did NOT come with it?
 *
 * The export re-encodes each asset and DROPS one over its byte ceiling, keeping
 * the spec that points at it — so `image: true` with no `assets.fg` is a real
 * export, and it draws the tile with no picture at all. That is indistinguishable
 * from a plain tile unless somebody says so, and the author is comparing it to
 * the drive row beside them, which draws the full composite.
 */
export function artDidNotTravel(meta: PortablePdappMetadata): boolean {
  const icon = meta.icon
  if (!icon) return false
  return (icon.bg.type === 'image' && !icon.assets?.bg) || (icon.image === true && !icon.assets?.fg)
}
